import { forwardRef, type ChangeEvent } from 'react';
import { toMobileDigits } from '@/lib/forms';
import { Input, type InputProps } from './Input';

export type PhoneInputProps = Omit<InputProps, 'leftIcon' | 'inputMode' | 'maxLength' | 'type'>;

/**
 * An Indian phone number: a fixed +91 in front, and ten digits after it.
 *
 * Anything that is not a digit is dropped as it is typed or pasted, and an
 * eleventh digit never appears. A number pasted whole — "+91 98765 43210",
 * "09876543210" — keeps the ten digits that matter (`toMobileDigits`). The
 * country code is drawn, not typed, so it can neither be left off nor doubled.
 * No `maxLength`: the browser would cut a pasted number short before the
 * digits were picked out; `toMobileDigits` is the cap.
 *
 * Works with `register()` as a plain input does: the cleaned value is written
 * back into the event before the caller's `onChange` sees it.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { onChange, placeholder = '98765 43210', ...props },
  ref,
) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    event.target.value = toMobileDigits(event.target.value);
    onChange?.(event);
  }

  return (
    <Input
      ref={ref}
      type="tel"
      inputMode="numeric"
      autoComplete="tel-national"
      placeholder={placeholder}
      leftIcon={<span className="text-sm font-[560] text-ink-2">+91</span>}
      onChange={handleChange}
      {...props}
    />
  );
});
